import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { postService } from '../services/api';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Search,
  FileText,
  Edit,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Filter,
  Calendar,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PostList = () => {
  const { user, canCreateContent } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1
  });
  const [pagination, setPagination] = useState({});

  // Verificar permissões
  useEffect(() => {
    if (!canCreateContent()) {
      navigate('/');
      return;
    }
  }, [canCreateContent, navigate]);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: filters.page,
        limit: 10,
        search: filters.search || undefined,
        status: filters.status || undefined,
        sortBy: 'createdAt',
        sortOrder: 'DESC'
      };

      // Se não for admin, carregar apenas posts do usuário
      if (user.role !== 'admin') {
        params.authorId = user.id;
      }

      const response = await postService.getPosts(params);

      if (response.success) {
        setPosts(response.data.posts);
        setPagination(response.data.pagination);
      } else {
        console.error('Erro ao carregar posts:', response.message);
      }
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
      alert('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  }, [filters, user.role, user.id]);

  // Carregar posts
  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleStatusChange = async (postId, newStatus) => {
    const action = newStatus === 'published' ? 'publicar' : 'despublicar';

    if (!window.confirm(`Tem certeza que deseja ${action} este post?`)) {
      return;
    }

    try {
      let response;

      if (newStatus === 'published') {
        response = await postService.publishPost(postId);
      } else {
        response = await postService.unpublishPost(postId);
      }

      if (response.success) {
        // Atualizar lista
        loadPosts();
      } else {
        alert('Erro ao alterar status: ' + response.message);
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status do post');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Tem certeza que deseja deletar o post "${post.title}"?`)) {
      return;
    }

    try {
      setDeleting(post.id);

      const response = await postService.deletePost(post.id);

      if (response.success) {
        // Remover da lista
        setPosts(prev => prev.filter(p => p.id !== post.id));
      } else {
        alert('Erro ao deletar: ' + response.message);
      }
    } catch (error) {
      console.error('Erro ao deletar post:', error);
      alert('Erro ao deletar post');
    } finally {
      setDeleting(null);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value // Reset page when other filters change
    }));
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: {
        variant: 'secondary',
        label: 'Rascunho',
        icon: <Edit className="h-3 w-3 mr-1" />,
        className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
      },
      published: {
        variant: 'default',
        label: 'Publicado',
        icon: <Eye className="h-3 w-3 mr-1" />,
        className: 'bg-church-green/10 text-church-green border-church-green/20'
      }
    };

    const { variant, label, icon, className } = config[status] || config.draft;

    return (
      <Badge variant={variant} className={`flex items-center ${className}`}>
        {icon}
        {label}
      </Badge>
    );
  };

  const canEditPost = (post) => {
    return user.role === 'admin' || post.authorId === user.id;
  };

  if (loading && posts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-church-green mx-auto"></div>
          <p className="text-gray-400">Carregando posts...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        title="Gerenciar Estudos"
        description="Lista de todos os estudos bíblicos para gerenciamento"
      />
      <div className="container mx-auto py-12 px-4 bg-black min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 pb-8 border-b border-white/5">
          <div>
            <h1 className="text-4xl font-heading font-bold text-white flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-church-green" />
              {user.role === 'admin' ? 'Todos os Posts' : 'Meus Posts'}
            </h1>
            <p className="text-gray-400 mt-2 font-light text-lg">
              Gerencie e organize seus estudos bíblicos
            </p>
          </div>

          <Button asChild size="lg" className="bg-church-green hover:bg-church-green/90 text-white border-none shadow-lg shadow-church-green/20 shrink-0 rounded-full px-8">
            <Link to="/admin/posts/new">
              <Plus className="h-5 w-5 mr-2" />
              Novo Post
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-8 bg-[#0a0a0a] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white font-heading">
              <Filter className="h-5 w-5 text-church-green" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Buscar por título ou conteúdo..."
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-church-green/50 focus:ring-church-green/20"
                  />
                </div>
              </div>

              <div className="sm:w-48">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-white/10 rounded-md bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-church-green/20 focus:border-church-green/50"
                >
                  <option value="" className="bg-[#0a0a0a]">Todos os status</option>
                  <option value="draft" className="bg-[#0a0a0a]">Rascunhos</option>
                  <option value="published" className="bg-[#0a0a0a]">Publicados</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Empty State */}
        {posts.length === 0 && !loading ? (
          <Card className="text-center py-16 bg-[#0a0a0a] border-white/10 border-dashed">
            <CardContent>
              <div className="w-20 h-20 bg-church-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-10 w-10 text-church-green" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white font-heading">
                {filters.search || filters.status ? 'Nenhum post encontrado' : 'Nenhum post criado ainda'}
              </h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                {filters.search || filters.status
                  ? 'Tente ajustar os filtros de busca para encontrar o que você procura'
                  : 'Comece criando seu primeiro estudo bíblico para compartilhar conhecimento!'
                }
              </p>
              {!filters.search && !filters.status && (
                <Button asChild className="bg-church-green hover:bg-church-green/90 text-white rounded-full">
                  <Link to="/admin/posts/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Post
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Posts Grid */}
            <div className="grid gap-6">
              {posts.map((post) => (
                <Card key={post.id} className="bg-[#0a0a0a] border-white/5 hover:border-church-green/30 transition-all duration-300 hover:shadow-lg hover:shadow-church-green/5 overflow-hidden group">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-4 mb-3">
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/admin/posts/edit/${post.id}`}
                              className="block group"
                            >
                              <h3 className="text-xl font-bold font-heading text-white group-hover:text-church-green transition-colors line-clamp-1">
                                {post.title}
                              </h3>
                            </Link>
                            {post.excerpt && (
                              <p className="text-gray-400 text-sm mt-2 line-clamp-2 font-light">
                                {post.excerpt}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0">
                            {getStatusBadge(post.status)}
                          </div>
                        </div>

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mt-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-church-green" />
                            <span className="text-gray-300">{post.author?.name}</span>
                            <Badge variant="outline" className="text-[10px] border-white/10 text-gray-400 h-5">
                              {post.author?.role === 'admin' ? 'Admin' : 'Autor'}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-church-green" />
                            <span className="text-gray-300">
                              Criado em {format(new Date(post.createdAt), "dd/MM/yyyy", { locale: ptBR })}
                            </span>
                          </div>

                          {post.publishedAt && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-church-green" />
                              <span className="text-gray-300">
                                Publicado {formatDistanceToNow(new Date(post.publishedAt), {
                                  addSuffix: true,
                                  locale: ptBR
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 pt-4 lg:pt-0 lg:pl-6 mt-4 lg:mt-0 justify-end lg:justify-start">
                        {canEditPost(post) && (
                          <>
                            <Button variant="ghost" size="sm" asChild className="text-gray-400 hover:text-white hover:bg-white/5">
                              <Link to={`/admin/posts/edit/${post.id}`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(
                                post.id,
                                post.status === 'published' ? 'draft' : 'published'
                              )}
                              title={post.status === 'published' ? 'Despublicar' : 'Publicar'}
                              className={post.status === 'published' ? 'text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10' : 'text-church-green hover:text-church-green/80 hover:bg-church-green/10'}
                            >
                              {post.status === 'published' ?
                                <EyeOff className="h-4 w-4" /> :
                                <Eye className="h-4 w-4" />
                              }
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(post)}
                              disabled={deleting === post.id}
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                            >
                              {deleting === post.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}

                        {post.status === 'published' && (
                          <Button variant="ghost" size="sm" asChild className="text-gray-400 hover:text-white hover:bg-white/5">
                            <Link
                              to={`/posts/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Card className="mt-8 bg-[#0a0a0a] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => handleFilterChange('page', pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="flex items-center gap-2 border-white/10 text-white hover:bg-white/5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>

                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span>
                        Página <span className="font-medium text-white">{pagination.currentPage}</span> de{' '}
                        <span className="font-medium text-white">{pagination.totalPages}</span>
                      </span>
                      <span className="hidden sm:inline">
                        ({pagination.totalPosts} {pagination.totalPosts === 1 ? 'post' : 'posts'} no total)
                      </span>
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => handleFilterChange('page', pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="flex items-center gap-2 border-white/10 text-white hover:bg-white/5"
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Loading Overlay */}
        {loading && posts.length > 0 && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <Card className="p-6 bg-[#0a0a0a] border-church-green/30">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-l-2 border-t-2 border-church-green"></div>
                <span className="text-gray-300">Atualizando lista...</span>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default PostList;